import { useCallback, useEffect, useMemo } from 'react';
import type { SortingTypes } from '@decisiv/ui-components/lib/components/Table';

import useDebouncedValue from 'hooks/useDebouncedValue';
import useQueryParams from 'hooks/useQueryParams';

import { API_SEARCH_DEBOUNCE_DELAY_MS } from '../constants';

const DIRECTION_MAPPING: Record<SortingTypes, SortDirection | undefined> = {
  ascending: 'ASC',
  descending: 'DESC',
  unsorted: undefined,
};

const REVERSE_DIRECTION_MAPPING: Record<SortDirection, SortingTypes> = {
  ASC: 'ascending',
  DESC: 'descending',
};

interface TableUrlState<TColumn extends string> {
  page: number;
  sort?: TColumn;
  sortDirection?: SortDirection;
  searchText: string;
}

const DEFAULTS = {
  page: 1,
  sort: undefined,
  sortDirection: undefined,
  searchText: '',
} as const;

export default function useTableUrlState<TColumn extends string = string>(
  key: string,
) {
  const [params, setParams] = useQueryParams<TableUrlState<TColumn>>(
    key,
    DEFAULTS as TableUrlState<TColumn>,
  );
  const { page, sort, sortDirection, searchText } = params;

  // Returns `null` when unsorted to preserve the previous behavior of `useSort`,
  // which kept Apollo mocks (built with `sort: null`) matching the active query.
  const apiSort = useMemo(
    () =>
      sort && sortDirection
        ? { column: sort, direction: sortDirection }
        : (null as unknown as undefined),
    [sort, sortDirection],
  );

  const defaultSort = useMemo(
    () =>
      sort && sortDirection
        ? {
            sortingColumn: sort,
            sortingType: REVERSE_DIRECTION_MAPPING[sortDirection],
          }
        : undefined,
    [sort, sortDirection],
  );

  // The KDS Table's `onSort` prop is typed as `(column: string, ...)`, so the
  // callback signature stays `string`; the column is narrowed to `TColumn`
  // when written into state since callers register columns from that enum.
  const onSort = useCallback(
    (column: string, direction: SortingTypes) => {
      if (direction === 'unsorted') {
        setParams((s) => ({
          ...s,
          sort: undefined,
          sortDirection: undefined,
          page: 1,
        }));
        return;
      }

      setParams((s) => ({
        ...s,
        sort: column as TColumn,
        sortDirection: DIRECTION_MAPPING[direction],
        page: 1,
      }));
    },
    [setParams],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setParams((s) => ({ ...s, page: newPage }));
    },
    [setParams],
  );

  const [liveSearch, setLiveSearch, debouncedSearch] = useDebouncedValue(
    searchText,
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  // When the debounced search value changes (the user typed), mirror it
  // into `searchText` (URL state) and reset to page 1. Only depending on
  // `debouncedSearch` here is intentional — including `searchText` would
  // make an external URL update fight with the back/forward sync effect
  // below.
  useEffect(() => {
    setParams((s) =>
      s.searchText !== debouncedSearch
        ? { ...s, searchText: debouncedSearch, page: 1 }
        : s,
    );
  }, [debouncedSearch, setParams]);

  // Sync external `searchText` changes (browser back/forward) into the
  // input. Guarded against the user-typed case (where `searchText` matches
  // the freshly-debounced value) to avoid a feedback loop.
  useEffect(() => {
    if (searchText !== debouncedSearch) {
      setLiveSearch(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return {
    page,
    setPage,
    apiSort,
    defaultSort,
    onSort,
    liveSearch,
    setLiveSearch,
    debouncedSearch,
  };
}
